import React from 'react';
import { orderBy, get, groupBy, uniqBy, sumBy, mapValues, keyBy, } from 'lodash';
import { Link } from 'react-router-dom';
import { format as formatDate, differenceInSeconds, addMonths, isWithinInterval, eachDayOfInterval, startOfDay, startOfMonth, endOfDay, endOfMonth, addDays, } from 'date-fns';
import { LineChart, CartesianGrid, XAxis, YAxis, Tooltip, Legend, Line, } from 'recharts';
import qs from 'qs';

import firebase from '../../firebase';
import { colors } from '../../shared/config';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import DateSelector from '../DateSelector';
import QuerySelector from '../QuerySelector';
import AdminPage from '../hocs/AdminPage';

const { keys, entries } = Object;
const db = firebase.firestore();
const usersRef = db.collection('users');
const pickingsRef = db.collection('pickings');
const dateDimensions = {
  day: {
    label: '日次',
    xs: _ => eachDayOfInterval({ start: _.startOn, end: _.endOn, }),
    xLabel: _ => formatDate(_, 'MM/dd'),
    start: _ => startOfDay(_),
    end: _ => endOfDay(_),
  },
  month: {
    label: '月次',
    xs: _ => uniqBy(eachDayOfInterval({ start: _.startOn, end: _.endOn, }), _ => formatDate(_, 'yyyy/MM')),
    xLabel: _ => formatDate(_, 'yyyy/MM'),
    start: _ => startOfMonth(_),
    end: _ => endOfMonth(_),
  },
};

const activities = _ => _.pickings.flatMap(_ => _.activities);
const hours = _ => sumBy(_.pickings, _ => differenceInSeconds(_.finishedAt.toDate(), _.startedAt.toDate()) / 3600);

const metrics = {
  pickingItemsCount: {
    label: '梱包件数',
    calc: _ => activities(_).length,
  },
  pickingItemsCountPerHour: {
    label: '1時間あたり梱包件数',
    calc: _ => Math.round(activities(_).length / hours(_)) || 0,
  },
  normalPickingItemsCount: {
    label: '宅配 梱包件数',
    calc: _ => activities(_).filter(_ => _.packageType === 'normal').length,
  },
  normalPickingItemsCountPerHour: {
    label: '宅配 1時間あたり梱包件数',
    calc: _ => Math.round(activities(_).filter(_ => _.packageType === 'normal').length / hours(_)) || 0,
  },
  nekoPickingItemsCount: {
    label: 'ネコポス 梱包件数',
    calc: _ => activities(_).filter(_ => _.packageType === 'neko').length,
  },
  nekoPickingItemsCountPerHour: {
    label: 'ネコポス 1時間あたり梱包件数',
    calc: _ => Math.round(activities(_).filter(_ => _.packageType === 'neko').length / hours(_)) || 0,
  },
  pickingsDuration: {
    label: '稼働時間',
    calc: _ => Math.round(hours(_) * 100) / 100,
  },
};
const dateDimensionOptions = entries(dateDimensions).map(([k, _]) => ({ label: _.label, value: k, }));

export default AdminPage(function AdminPickingStats (props) {
  const { history, location } = props;
  const queryParams = qs.parse(decodeURI(location.search.slice(1)));
  const {
    startOn: startOnString = formatDate(addMonths(new Date(), -3), 'yyyy-MM-dd'),
    endOn: endOnString = formatDate(new Date(), 'yyyy-MM-dd'),
    dateDimension: dateDimensionKey = 'day',
  } = queryParams;
  const dateDimension = dateDimensions[dateDimensionKey];
  const startOn = startOfDay(new Date(startOnString));
  const endOn = endOfDay(new Date(endOnString));
  const users = useCollectionSubscription(usersRef);
  const staffs = users.filter(_ => _.admin || _.isStaff);
  const staffsById = keyBy(staffs, 'id');
  const pickings = useCollectionSubscription(pickingsRef.where('createdAt', '>=', startOn).where('createdAt', '<=', endOn), [startOnString, endOnString]);
  const filteredPickings = pickings.filter(_ => _.finishedAt != null);
  const pickingsGroupedByUserId = groupBy(filteredPickings, 'userId');
  const xs = dateDimension.xs({ startOn, endOn });

  return (
    <div className="admin-picking-stats container-fluid">
      <div className="p-4 bg-white my-4">
        <div className="d-flex justify-content-center mb-3">
          <h4>ピッキング統計</h4>
        </div>
        <div className="d-flex justify-content-start mb-3 position-relative" style={{ zIndex: 2 }}>
          <div className="d-flex align-items-end flex-wrap">
            <QuerySelector paramName="dateDimension" options={dateDimensionOptions} {...{ history, location }} defaultValue="day" />
            <DateSelector className="ml-2" paramName="startOn" label="開始日" history={history} location={location} defaultValue={addMonths(new Date(), -3)} />
            <DateSelector className="ml-2" paramName="endOn" label="終了日" history={history} location={location} defaultValue={new Date()} invalid={startOn > endOn} />
          </div>
        </div>
        <div className="mt-4 d-flex flex-wrap justify-content-around">
          {
            entries(metrics).map(([metric, { label, calc }], i) => {
              const data = xs
                .map((xItem) => {
                  return {
                    x: dateDimension.xLabel(xItem, 'MM/dd'),
                    ...(
                      staffs.reduce((x, staff) => {
                        const rangePickings = get(pickingsGroupedByUserId, staff.id, []).filter(_ => isWithinInterval(_.createdAt.toDate(), { start: dateDimension.start(xItem), end: dateDimension.end(xItem) }));
                        return {
                          ...x,
                          [staff.displayName]: calc({ pickings: rangePickings }),
                        };
                      }, {})
                    ),
                  };
                });
              const sortedStaffData = orderBy(staffs.map((staff) => {
                return {
                  ...staff,
                  value: sumBy(data, staff.displayName),
                };
              }).filter(_ => _.value), 'value', 'desc');
              return (
                <div key={i} className="mt-4">
                  <div className="card p-3">
                    <h4 className="mb-3">{label}</h4>
                    <LineChart width={550} height={500} data={data} style={{ zIndex: 1 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="x" />
                      <YAxis tick={{ fontSize: 14.5 }} height={500} />
                      <Tooltip />
                      <Legend />
                      {
                        sortedStaffData.map((staff, i) => {
                          return (
                            <Line key={staff.id} type="monotone" dataKey={staff.displayName} stroke={colors[i % colors.length]} strokeWidth={2} />
                          );
                        })
                      }
                    </LineChart>
                  </div>
                </div>
              );
            })
          }
        </div>
      </div>
    </div>
  );
});
